// @ts-strict-ignore
import { ProjectModel } from '@project/model/project.model';
import { ArticleVO } from '@article/model/vo/article.vo';
import { IBaseArticleProjectProcessor } from '@article/model/i-base-article-project.processor';
import { BaseArticleProjectProcessorFactory } from '@article/model/base-article-project-processor-factory';
import { ArticleConstants } from '@article/constants/article-constants';
import { ArticleManager } from '@article/manager/article.manager';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ArticleContainerAttachedService } from '@project/components/article/service/article-container-attached.service';
import { Log } from '@shared/debug/log';
import { Subscription } from 'rxjs';
import { ProjectModelService } from '@project/service/project-model.service';
import { MiniTerminalBlocksArticleManager } from '@article/manager/mini-terminal-blocks-article.manager';
import { MiniTerminalBlocksProjectCreationService } from './mini-terminal-blocks-project-creation.service';
import { ProjectType } from '@project/model/project-type';

export class McsGdsProjectCreationProcessor {
  private readonly ATTACH_MCS_GDS_ARTICLE_MESSAGE: string = 'addMcsGdsArticle';

  private _baseArticleProjectProcessor: IBaseArticleProjectProcessor;
  private _processedArticle: ArticleVO;

  private _freeMountingAttached: boolean = false;
  private _attachedArticleContainerSubscription: Subscription;

  constructor(
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private alertHelper: AlertHelper,
    private miniTerminalBlocksArticleManager: MiniTerminalBlocksArticleManager,
    private baseArticleProjectProcessorFactory: BaseArticleProjectProcessorFactory,
    private _articleContainerAttachedService: ArticleContainerAttachedService,
    private miniTerminalBlocksProjectCreationService: MiniTerminalBlocksProjectCreationService
  ) {
    this._attachedArticleContainerSubscription = this._articleContainerAttachedService.attachedArticleContainer$.subscribe({
      next: () => this.articleContainerAttached(),
      error: error => Log.error('Error in attached article container subscription: ', error)
    })
  }

  public async assembleProject(article: ArticleVO, projectType: ProjectType): Promise<void> {
    this._processedArticle = article;
    this.updateProjectInfoPropertiesByMcsGdsArticleData(article);
    this.projectModelService.currentProjectType = projectType;
    await this.projectModelService.updateBaseArticle();

    // adding articles on rail is forbidden in MCS project
    // so free-mounting processor is used to add given article on rail
    this._baseArticleProjectProcessor = this.baseArticleProjectProcessorFactory.createBaseArticleManagerByProjectType(
      "FREE_MOUNTING", this.ATTACH_MCS_GDS_ARTICLE_MESSAGE);
    await this.createFreeMounting();
  }

  private updateProjectInfoPropertiesByMcsGdsArticleData(article: ArticleVO): void {
    ProjectUtils.setMcsGdsProjectData(this.projectModel.currentProject, article.identity, article.version, article.articleNo);
  }

  private async createFreeMounting(): Promise<void> {
    this._freeMountingAttached = false;
    try {
      await this.miniTerminalBlocksArticleManager.quickLoadArticle({
        data: ArticleConstants.DEFAULT_FREE_MOUNTING_ARTICLE_ID,
        quickLoadType: ArticleManager.QUICK_LOAD_TYPE_LOAD_BY_ARTICLE_ID,
      });
    } catch (error) {
      // TODO SLK Is this call correct?
      this.miniTerminalBlocksProjectCreationService.projectCreationFailureHandler();
    }
  }

  private async articleContainerAttached(): Promise<void> {
    if (this._freeMountingAttached) {
      return
    }
    this._freeMountingAttached = true;

    const projectData: any[] = this._baseArticleProjectProcessor.prepareProjectDataBySelection(this._processedArticle);

    try {
      await this._baseArticleProjectProcessor.addArticleByProjectData(projectData[0], this._processedArticle);
      this.addArticleProcess_success();
    } catch (error) {
      await this.addArticleProcess_failure();
    }
  }

  private addArticleProcess_success(): void {
    this.projectModel.notifyCurrentProjectChanged();
    this.clear();
    this.projectModelService.notifyMcsGdsProjectCreated();
  }

  private async addArticleProcess_failure(): Promise<void> {
    await this.projectModelService.clearCurrentProject(false);
    this.alertHelper.showError('createMcsGdsDialog.error_connect.title', 'createMcsGdsDialog.error_connect.message');
  }

  public clear(): void {
    this._processedArticle = null;
    this._baseArticleProjectProcessor = null;
    this._freeMountingAttached = false;
    this._attachedArticleContainerSubscription?.unsubscribe();
    this._attachedArticleContainerSubscription = undefined;
  }
}
